/**
 * Loja normalizada para COMPARAÇÃO: sem espaço, sem acento, maiúscula.
 * Ex.: 'Escritório' -> 'ESCRITORIO'.
 *
 * ⚠️⚠️ EXTRAÍDO EM 31/08/2026 porque virou o SEGUNDO uso (o piloto do
 *    Chamados, no fim do arquivo). A regex de acento estava escrita duas vezes, e
 *    duas cópias de uma normalização de loja é como se perde o acento do
 *    ESCRITÓRIO num dos lados — a armadilha que este portal já documenta em
 *    vários módulos.
 *
 * ⚠️ Espelha o `_lojaCmp` do `server/utils.js`. A faixa derruba todo caractere
 *    não-ASCII DEPOIS do `NFD`, que separa a letra do sinal — por isso ela remove
 *    só os acentos, e não a letra.
 */
// ⚠️⚠️ MONTADA COM `new RegExp`, e NÃO como literal — mesmo motivo já registrado
//    no `ChamadosAtivos.js`: escrita como `/[^\x00-\x7f]/`, a faixa entra no
//    arquivo como DOIS CARACTERES DE CONTROLE INVISÍVEIS assim que alguma
//    ferramenta reescreve o fonte. Aconteceu aqui em 31/08/2026: a regex virou
//    `/[^ -]/` e parou de remover acento — sem erro, sem aviso, e com o
//    ESCRITÓRIO deixando de casar. Em string, os `\x` são interpretados pelo
//    RegExp e o fonte fica só com ASCII legível.
// ⚠️ A faixa `\x00-\x7f` É o padrão aqui: "tudo que não é ASCII" é exatamente o que
//    se quer remover depois do NFD. Não há bug a corrigir — e trocar a faixa mexeria
//    num comparador que já falhou calado uma vez (ver acima). Por isso a supressão do
//    aviso é DA LINHA, nunca da regra.
// eslint-disable-next-line no-control-regex
const NAO_ASCII = new RegExp('[^\x00-\x7f]', 'g');

function lojaCmp(loja) {
    return (loja || '')
        .trim()
        .normalize('NFD').replace(NAO_ASCII, '')
        .toUpperCase();
}

// Usuário "preso à loja": não-Admin com loja específica (≠ ESCRITÓRIO).
// Espelha o recorte do backend (server/utils.js › escopoLojaSql / podeVerLoja).
// Usado para ocultar módulos centrais (Automações, Processo Seletivo, etc.)
// de usuários de loja (ex.: Linhares).
export function isUsuarioPresoLoja(user) {
    if (!user) return false;
    if ((user.perfil || '').toLowerCase() === 'admin') return false;
    const loja = lojaCmp(user.loja);
    return !!loja && loja !== 'ESCRITORIO';
}

/**
 * Usuário "de escritório": alcança a rede inteira, sem recorte por loja.
 *
 * ⚠️ É o COMPLEMENTO do `isUsuarioPresoLoja` acima, e de propósito: os dois
 *    lados da mesma régua num arquivo só. Escrito separado porque o que a Home
 *    pergunta é a pergunta afirmativa ("é do escritório?"), e `!preso` numa
 *    condição de render se lê errado na revisão.
 *
 * ⚠️ Espelha a ISENÇÃO do `escopoLojaSql` do backend (`server/utils.js` — Admin
 *    OU loja ESCRITÓRIO devolvem `null`, isto é, sem filtro). Se um dia as duas
 *    divergirem, a tela mostra uma aba que a rota recorta (ou o contrário).
 *
 * 1º consumidor: a aba **Escritório** da Home (`home/abas-home.js`).
 */
export function ehEscritorio(user) {
    if (!user) return false;
    if ((user.perfil || '').toLowerCase() === 'admin') return true;
    return lojaCmp(user.loja) === 'ESCRITORIO';
}

// =====================================================================
// Chamados — o PORTÃO POR LOJA. ✅ PILOTO ENCERRADO em 2026-09-10 (Julia): a
// lista está VAZIA, logo todas as lojas alcançam o módulo. TEMPORÁRIO — o
// arquivo só pode ser limpo DEPOIS deste desligamento, nunca antes.
// =====================================================================
/**
 * As lojas que já usam o módulo Chamados.
 *
 * ⚠️⚠️ ESPELHO de `server/chamados/chamados-piloto.js` › `LOJAS_PILOTO`. Ele vive
 *    aqui porque o menu lateral decide o que mostrar SEM chamar a API — e um
 *    item de menu que leva a um 403 é pior que nenhum item.
 *    Divergir dá os dois sintomas ruins: menu que não abre, ou loja liberada no
 *    servidor que não acha o módulo na tela. Há teste travando as duas listas.
 *
 * ⚠️ Lista VAZIA = piloto encerrado, todas as lojas entram (igual ao backend).
 */
export const LOJAS_PILOTO_CHAMADOS = [];

/**
 * Este usuário já alcança o módulo Chamados?
 *
 * ⚠️ NÃO é permissão — é disponibilidade. Quem pode o quê continua vindo do
 *    perfil; isto responde "o módulo já chegou na loja dele?".
 *
 * ⚠️ O ESCRITÓRIO entra sempre: é lá que está todo o lado que ATENDE o chamado
 *    (Gerente TI, os técnicos, Admin, Diretoria). Sem isso o piloto travaria a
 *    triagem, e as três lojas abririam chamado para ninguém.
 *
 * ⚠️ A comparação tira acento e caixa, como o `isUsuarioPresoLoja` acima: o
 *    cadastro grava 'ESCRITÓRIO' COM acento, e comparar cru volta falso em
 *    silêncio.
 */
export function lojaNoPilotoChamados(user) {
    if (!LOJAS_PILOTO_CHAMADOS.length) return true;
    const loja = lojaCmp(user?.loja);
    if (loja === 'ESCRITORIO') return true;
    return LOJAS_PILOTO_CHAMADOS.includes(loja);
}
